"use server";
import fs from "fs";
import path from "path";
import crypto from "crypto";

import * as csv from "fast-csv";
// TODO A conserver (Génération fichier référence)
// import fs from "fs/promises";

import { SequenceToExport } from "@/utils/types/sequence";
import prisma from "@/lib/db";
import { MetaDataRow } from "@/types/metadata";
import {
  getSeqIdFromFastaLineDefinition,
  makeSequencesFlat,
  validateFastaLineDefinition,
  validateFastaLineSequence,
} from "@/utils/fasta";
import {
  FastaFile,
  ValidationSuccessCsv,
  ValidationError,
  ValidationFailure,
  ValidationSuccess,
  ValidationFailureCsv,
} from "@/utils/interface";
import {
  searchForDuplicates,
  validateMetadataRow,
} from "@/utils/metadataGlobalValidation";
import { formatSeqID, validSeqId } from "@/utils/metadataValidationFunctions";
import {
  //TODO A conserver (Génération fichier référence)
  // deleteObject,
  downloadFile,
  getObject,
  listObjectsRemote,
  presignedGetObjectTmp,
  putObject,
  refExists,
} from "@/utils/minio";
import { timeoutPromise } from "@/utils/requests";
import { getTemporaryTableColumns } from "@/utils/temporaryTableColumns";
import {
  // TODO A conserver (Génération fichier référence)
  // $Enums,
  Prisma,
  viruses,
  status,
  severity,
  Import,
} from "@prisma/client";
import { verifyServerSession } from "@/utils/auth";

export const initImport = async (): Promise<Import> => {
  try {
    const { loggedInUuid } = await verifyServerSession();
    const currentImport = await prisma.import.create({
      data: { User_Keycloak_ID: loggedInUuid, FASTA_URL: null, CSV_URL: null },
    });
    return currentImport;
  } catch (error: any) {
    throw new Error("Failed to insert row in Import table", error);
  }
};

export const updateImportPublicData = async (
  uuid: string,
  publicData: boolean,
): Promise<Import> => {
  const { loggedInUuid } = await verifyServerSession();
  return await prisma.import.update({
    where: {
      uuid: uuid,
      User_Keycloak_ID: loggedInUuid,
    },
    data: {
      publicData: publicData,
    },
  });
};

/**
 * Get a single import by id and user uuid.
 */
export const getImportByUser = async (id: number) => {
  const { loggedInUuid } = await verifyServerSession();
  return await prisma.import.findUnique({
    where: {
      ID: id,
      User_Keycloak_ID: loggedInUuid,
    },
  });
};

/**
 * Get a single import by UUID and user uuid.
 */
export const getImportByUserUuid = async (uuid: string) => {
  const { loggedInUuid } = await verifyServerSession();
  return await prisma.import.findUnique({
    where: {
      uuid: uuid,
      User_Keycloak_ID: loggedInUuid,
    },
  });
};

/**
 * Get Import by UUID only (internal use).
 * WARNING: Only use this for internal operations where ownership is already verified.
 */
// export const getImportByUuid = async (uuid: string) => {
//   return await prisma.import.findUnique({
//     where: {
//       uuid: uuid,
//     },
//   });
// };

export const updateImportFiles = async (
  import_uuid: string,
  data: {
    FASTA_URL: string;
    CSV_URL: string;
  },
) => {
  try {
    const { loggedInUuid } = await verifyServerSession();
    await prisma.import.update({
      where: { uuid: import_uuid, User_Keycloak_ID: loggedInUuid },
      data: data,
    });
  } catch (error: any) {
    throw new Error("Failed to update row in Import table", error);
  }
};

export const validateFastaData = async (
  fileName: string,
): Promise<ValidationSuccess | ValidationFailure> => {
  await verifyServerSession();
  return new Promise(async (resolve, reject) => {
    try {
      const fastaBuffer = (await downloadFile(
        process.env.MINIO_BUCKET_TMP!,
        fileName,
      )) as Buffer;
      const lines = fastaBuffer.toString().split(/\r?\n/);
      const errors: any = [];

      while (lines.length > 0 && lines[lines.length - 1]?.trim() === "") {
        lines.pop();
      }

      if (lines.every((line) => line.trim() === "")) {
        errors.push({ lineNumber: 0, error: "This file is empty" });
      }

      if (lines.length === 1) {
        errors.push({
          lineNumber: 0,
          error:
            "This file has only one line. At least two are expected, one definition, and one sequence.",
        });
      }

      // Rules:
      // 1. Expected identifier line starting with >
      // 2. Identifier line must not contain white spaces
      // 3. Sequence line must not contain white spaces
      // 4. Invalid characters in sequence line
      // 5. Duplicate sequence identifier
      // 6. Invalid sequence length
      // 7. Invalid format for sequence identifier

      let currentIdentifier = null;
      const sequenceIdentifiers = new Set<string>();

      for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const lineNumber = i + 1;

        if (i === 0) {
          currentIdentifier = validateFastaLineDefinition(
            line,
            lineNumber,
            errors,
            sequenceIdentifiers,
          );
          continue;
        }

        if (i === 1) {
          validateFastaLineSequence(
            line,
            lineNumber,
            errors,
            currentIdentifier,
          );
          continue;
        }

        if (line.startsWith(">")) {
          currentIdentifier = validateFastaLineDefinition(
            line,
            lineNumber,
            errors,
            sequenceIdentifiers,
          );
        } else {
          validateFastaLineSequence(
            line,
            lineNumber,
            errors,
            currentIdentifier,
          );
        }
      }

      if (errors.length > 0) {
        resolve({
          status: "error",
          message: "Validation errors in some rows",
          errors: errors, // Convert the formatted error string into an array of strings
        });
      } else {
        resolve({
          status: "success",
          rowCount: lines.length / 2,
          seq_ids: Array.from(sequenceIdentifiers),
        });
      }
    } catch (err: any) {
      console.error(`Error download file ${fileName}:`, err);
      throw new Error("Failed to download fasta files", err);
    }
  });
};

const convertEmptyToNull = (value: any): any => {
  if (typeof value === "string" && value.trim() === "") {
    return null;
  }
  return value;
};

const convertToNumberIfPossible = (key: string, value: any): any => {
  // List of numbers or decimal keys to convert
  // TODO a modifier pour utiliser le typescript ?
  const intDecimalKeys = [
    "id",
    "sampling_date",
    "sequencing_date",
    "ct",
    "passage_nb",
    "read_nb",
    "mean_cov",
    "release_date",
    "release_gb_date",
    "serotype",
    "cov10x",
    "cov50x",
    "cov100x",
    "mean_qs",
    "latitude",
    "longitude",
    "isolate",
  ];
  if (
    intDecimalKeys.includes(key) &&
    typeof value === "string" &&
    !isNaN(Number(value))
  ) {
    return Number(value);
  }
  if (value !== null && typeof value === "string") {
    return value.trim();
  }
  return value;
};

export const validateCsvData = async (
  fileName: string,
  hasRolePublicDataInsertion: boolean,
  expectedHeaders: string[],
  contextValue: string,
): Promise<ValidationSuccessCsv | ValidationFailureCsv> => {
  await verifyServerSession();
  return new Promise(async (resolve) => {
    const results: MetaDataRow[] = [];
    let errors: ValidationError[] = [];
    const warnings: ValidationError[] = [];
    // TODO to keep when thesaurus integrations start
    // const validSampleTypes = await getThesaurusData("sample");
    // const validHosts = await getThesaurusData("host");
    // const validSequencingStrategies = await getThesaurusData(
    //   "sequencing_strategy"
    // );
    // const validSequencingTechnologies = await getThesaurusData(
    //   "sequencing_technology"
    // );
    // console.log("validSampleTypes", validSampleTypes);
    let presentHeaders: string[] = [];
    console.log(`validateCsvData ${fileName}...`);
    try {
      const csvBuffer = (await downloadFile(
        process.env.MINIO_BUCKET_TMP!,
        fileName,
      )) as Buffer;
      let lineNumber = 0;
      // Boolean to determine if user will import public data or not
      // If true, some validations will be skipped (role in keycloak: public_data_batch_insertion)
      let publicDataInsertion = false;
      const seqIdsInFile: { data: any; lineNumber: number; seq_id: string }[] =
        [];
      const sampleIdsInFile: {
        data: any;
        lineNumber: number;
        seq_id: string;
      }[] = [];
      const gbIdsInFile: {
        data: string;
        lineNumber: number;
        seq_id: string;
      }[] = [];

      const csvParser = csv
        .parse({
          quote: '"',
          headers: (headers) => headers.map((header) => header?.trim()),
        })
        .on("error", (error) => {
          console.error("Stream error:", error);
          resolve({
            status: "error",
            message: error.message,
            errors: errors,
          });
        })
        .on("headers", (headers: string[]) => {
          const missingHeaders = expectedHeaders.filter(
            (header) => !headers.includes(header),
          );
          const extraHeaders = headers.filter(
            (header) => !expectedHeaders.includes(header),
          );

          // Check if the CSV file contains certain headers and role
          const searchedHeaders = ["genotype", "serotype", "lineage"];
          let missingSearchedHeaders: string[] = [];

          if ((contextValue as viruses) === "DENV") {
            if (hasRolePublicDataInsertion) {
              const presentSearchedHeaders = searchedHeaders.filter((header) =>
                headers.includes(header),
              );
              missingSearchedHeaders = searchedHeaders.filter(
                (header) => !headers.includes(header),
              );
              presentHeaders = presentSearchedHeaders;

              // Only set publicDataInsertion to true if ALL 3 headers are present
              if (presentSearchedHeaders.length === 3) {
                publicDataInsertion = true;
                searchedHeaders.forEach((header) => {
                  const index = extraHeaders.indexOf(header);
                  if (index !== -1) {
                    extraHeaders.splice(index, 1);
                  }
                });
              }
            }
          }
          if ((contextValue as viruses) === "CHIKV") {
            if (hasRolePublicDataInsertion) {
              publicDataInsertion = true;
            }
          }

          let errors: any[] = [];

          if (missingHeaders.length !== 0) {
            errors.push({
              lineNumber: 0,
              seq_id: "",
              error: `Please add the following required headers: ${missingHeaders.join(", ")}`,
            });
          }

          // partial public data insertion headers (1 or 2 out of 3)
          if (
            (contextValue as viruses) === "DENV" &&
            hasRolePublicDataInsertion &&
            missingSearchedHeaders.length > 0 &&
            missingSearchedHeaders.length < 3
          ) {
            const presentSearchedHeaders = searchedHeaders.filter((header) =>
              headers.includes(header),
            );
            errors.push({
              lineNumber: 0,
              seq_id: "",
              error: `For public data batch insertion, you must include ALL of these headers: ${searchedHeaders.join(", ")}. Currently present: ${presentSearchedHeaders.join(", ")}. Missing: ${missingSearchedHeaders.join(", ")}. Please either add the missing headers for batch import OR remove ${presentSearchedHeaders.join(", ")} for private import.`,
            });
          }

          if (extraHeaders.length !== 0) {
            let extraHeadersList: string[] = [];
            let filteredExtraHeaders = extraHeaders;
            if ((contextValue as viruses) === "DENV") {
              // Delete the elements of extraHeaders that are present in presentHeaders (genotype and serotype)
              // Then push into error list the elements of presentHeaders if user have not role public data insertion
              filteredExtraHeaders = extraHeaders.filter(
                (header) => !presentHeaders.includes(header),
              );
              if (!hasRolePublicDataInsertion && presentHeaders.length > 0) {
                extraHeadersList.push(presentHeaders.join(","));
              }
            }

            if (filteredExtraHeaders.length !== 0) {
              extraHeadersList.push(filteredExtraHeaders.join(","));
            }

            if (extraHeadersList.length !== 0) {
              errors.push({
                lineNumber: 0,
                seq_id: "",
                error: `Please remove the following headers: ${extraHeadersList.join(
                  ",",
                )}`,
              });
            }
          }

          if (errors.length > 0) {
            resolve({
              status: "error",
              message: "Invalid headers",
              errors: errors,
            });
          }
        })
        .on("data", (row: MetaDataRow) => {
          lineNumber++;
          // Convert strings to their correct types before sending them to the validation process
          let formattedRow: MetaDataRow = Object.fromEntries(
            Object.entries(row).map(([key, value]) => {
              // convert empty strings to null, then check for numbers
              let newValue = convertEmptyToNull(value);
              newValue = convertToNumberIfPossible(key, newValue);
              // Replace all double quotes with nothing
              if (typeof newValue === "string") {
                newValue = newValue.replace(/"/g, "");
              }
              return [key, newValue];
            }),
          ) as MetaDataRow;
          if (formattedRow.severity === null) {
            formattedRow.severity = "unknown" as severity;
          }
          //console.log("formattedRow", formattedRow);
          seqIdsInFile.push({
            data: formattedRow.seq_id,
            lineNumber: lineNumber,
            seq_id: formattedRow.seq_id,
          });
          sampleIdsInFile.push({
            data: formattedRow.sample_id,
            lineNumber: lineNumber,
            seq_id: formattedRow.seq_id,
          });

          // Validate each row here
          const validationErrors = validateMetadataRow(
            formattedRow,
            lineNumber,
            formattedRow.seq_id,
            publicDataInsertion,
            contextValue,
          );

          if (formattedRow.gb_id != null) {
            gbIdsInFile.push({
              data: formattedRow.gb_id,
              lineNumber: lineNumber,
              seq_id: formattedRow.seq_id,
            });
          }

          if (validationErrors.errors.length === 0) {
            results.push(formattedRow);
          } else {
            validationErrors.errors.forEach((error) => {
              errors.push(error);
            });
          }
          if (validationErrors.warnings.length > 0) {
            validationErrors.warnings.forEach((warning) => {
              warnings.push(warning);
            });
          }
        })
        .on("end", async (rowCount: number) => {
          console.log(`Parsed ${rowCount} rows`);
          if (rowCount === 0) {
            return resolve({
              status: "error",
              message: "Empty file",
              errors: [{ lineNumber: 0, seq_id: "", error: "File is empty" }],
            });
          }
          // Check for duplicates in file and in database
          await searchForDuplicates(seqIdsInFile, "seq_id", errors);
          await searchForDuplicates(sampleIdsInFile, "sample_id", errors);
          await searchForDuplicates(gbIdsInFile, "gb_id", errors);

          // Résultat final
          if (errors.length > 0) {
            return resolve({
              status: "error",
              message: "Validation errors",
              publicDataInsertion: publicDataInsertion,
              errors,
            });
          }

          resolve({
            status: warnings.length > 0 ? "warning" : "success",
            rowCount,
            data: results,
            warnings: warnings.length > 0 ? warnings : [],
            serotypeGenotypeHeadersArePresent: presentHeaders.length > 0,
            publicDataInsertion: publicDataInsertion,
          });
        });
      csvParser.write(csvBuffer);
      csvParser.end();
    } catch (err: any) {
      console.error(`Error download file ${fileName}:`, err);
      throw new Error("Failed to upload csv files", err);
    }
  });
};

export const validateIsoSequenceInFiles = async (
  repCsvValidation: ValidationSuccess,
  repFastaValidation: ValidationSuccess,
) => {
  await verifyServerSession();
  console.log("validateIsoSequenceInFiles...");

  // Ensure that the necessary data is present
  if (!repCsvValidation.data || !repFastaValidation.seq_ids) {
    return;
  }

  const csvSeqIds =
    repCsvValidation.data?.map((item: any, index: number) => ({
      seq_id: item.seq_id,
      lineNumber: index + 1,
    })) || [];

  const fastaSeqIds =
    repFastaValidation.seq_ids?.map((seq_id: string, index: number) => ({
      seq_id,
      lineNumber: index + 1,
    })) || [];

  // Identify seq_ids present in CSV but not in FASTA
  const csvNotInFasta = csvSeqIds.filter(
    (csvItem) =>
      !fastaSeqIds.some((fastaItem) => fastaItem.seq_id === csvItem.seq_id),
  );

  // Identify seq_ids present in FASTA but not in CSV
  const fastaNotInCsv = fastaSeqIds.filter(
    (fastaItem) =>
      !csvSeqIds.some((csvItem) => csvItem.seq_id === fastaItem.seq_id),
  );

  const csvErrors: ValidationError[] = [];
  const fastaErrors: ValidationError[] = [];

  // Handle errors for CSV entries not in FASTA
  if (csvNotInFasta.length > 0) {
    csvNotInFasta.forEach((csvItem) => {
      csvErrors.push({
        lineNumber: csvItem.lineNumber,
        seq_id: csvItem.seq_id,
        error: `The seq_id ${csvItem.seq_id} in CSV is not present in the FASTA file.`,
      });
    });
  }

  // Handle errors for FASTA entries not in CSV
  if (fastaNotInCsv.length > 0) {
    fastaNotInCsv.forEach((fastaItem) => {
      fastaErrors.push({
        lineNumber: fastaItem.lineNumber,
        seq_id: fastaItem.seq_id,
        error: `The seq_id ${fastaItem.seq_id} in FASTA is not present in the CSV file.`,
      });
    });
  }

  const csvValidationErrors: ValidationFailure = {
    status: "error",
    message: "Some seq_ids in the CSV file are not present in the FASTA file.",
    errors: csvErrors,
  };

  const fastaValidationErrors: ValidationFailure = {
    status: "error",
    message: "Some seq_ids in the FASTA file are not present in the CSV file.",
    errors: fastaErrors,
  };

  // Determine the final status and return the validation results
  if (csvNotInFasta.length > 0 || fastaNotInCsv.length > 0) {
    return {
      status: "error",
      repCsvValidation: csvErrors.length
        ? { ...repCsvValidation, ...csvValidationErrors }
        : repCsvValidation,
      repFastaValidation: fastaErrors.length
        ? { ...repFastaValidation, ...fastaValidationErrors }
        : repFastaValidation,
      csvNotInFasta,
      fastaNotInCsv,
      message: "There are seq_id mismatches between CSV and FASTA files.",
    };
  }

  return {
    status: "success",
    message: "All seq_id(s) match between CSV and FASTA validation.",
  };
};

export const generateArbogenId = async (data: MetaDataRow[] | undefined) => {
  await verifyServerSession();
  if (!data) return [];

  const updatedData = data.map((row) => {
    const arbogen_id = `${row.sample_id}|${row.infection_loc_admin0}|${
      row.sampling_date ?? "UNK"
    }`;
    return { ...row, arbogen_id };
  });
  return updatedData;
};

export const splitRawFastaFile = async (fileName: string, prefix: string) => {
  await verifyServerSession();
  const filesWritten = [];

  try {
    const fastaBuffer = (await timeoutPromise(
      downloadFile(process.env.MINIO_BUCKET_TMP!, fileName),
    )) as Buffer;

    const flattenedFasta = makeSequencesFlat(fastaBuffer.toString());
    const sequences = flattenedFasta
      .split(">")
      .filter((seq) => seq.trim().length > 0);

    for (const seq of sequences) {
      const lines = seq.split("\n");
      const header = lines?.shift()?.trim(); // Manipulate data to retrive the header
      if (!header || !validSeqId(header)) {
        throw new Error(
          `Failed to split fasta files, the header ${header} is invalid.`,
        );
      }

      const seqId = getSeqIdFromFastaLineDefinition(header, false);
      const identifier = formatSeqID(seqId);
      // Before, we were taking only the first part of the SeqID (before
      // the first "|"). I changed it to make it easier to retrieve files
      // from MinIO. Now we take the whole SeqID.
      //const identifier = formatSeqID(header.split("|")[0]);

      const sequence = lines.join("\n").trim();
      const outputFileName = `${prefix}/${identifier}.fasta`;

      // Upload sequence into a bucket
      try {
        await timeoutPromise(
          putObject(
            process.env.MINIO_BUCKET_LOADER!,
            outputFileName,
            [">" + formatSeqID(header), sequence].join("\n"),
          ),
        );
        filesWritten.push({
          seq_id: formatSeqID(seqId),
          fasta_url: outputFileName,
        });
      } catch (err: any) {
        console.error(`Error writing file ${outputFileName}:`, err);
        throw new Error("Failed to upload fasta files", err);
      }
    }
  } catch (err: any) {
    console.error(`Error download file ${fileName}:`, err);
    throw new Error("Failed to download fasta files", err);
  }
  return { status: "200", data: filesWritten };
};

export const insertFastaUrlInTempTable = async (
  urlList: FastaFile[],
  temporaryTableName: string,
  importId: number,
) => {
  await verifyServerSession();
  // Update each line of temp import with fasta url
  for (const url of urlList) {
    try {
      const nbRows = await timeoutPromise(
        prisma.$executeRaw`
          UPDATE import_temp.${Prisma.raw(temporaryTableName)}
          SET fasta_url=${url.fasta_url}, import_id=${importId}
          WHERE seq_id=${url.seq_id}
        `,
      );
      // TODO: if nbRows = 0 throw new Error ?
    } catch (error: any) {
      console.error(
        `Failed to insert: ID=${url.seq_id}, fasta_url=${url.fasta_url}`,
        error,
      );
      throw new Error(
        `Failed to insert: ID=${url.seq_id}, fasta_url=${url.fasta_url}, error=${error}`,
      );
    }
  }
};

const computeMD5 = (data: Buffer) => {
  const uint8Data = new Uint8Array(data);
  return crypto.createHash("md5").update(uint8Data).digest("hex");
};
export const addRefDataInMinio = async () => {
  await verifyServerSession();
  console.log("Checking files in MinIO...");
  const REF_DATA_DIR = path.resolve(
    process.cwd(),
    "public",
    "assets",
    "arbogen_ref_data",
  );

  try {
    // List existing files in MinIO
    const existingFiles = await listObjectsRemote(
      process.env.MINIO_BUCKET_LOADER!,
      "reference/",
    );
    console.log("Existing files in MinIO:", existingFiles);
    const minioFilesMap: Record<string, string> = {};

    for (const file of existingFiles) {
      const fileStream = await getObject(
        process.env.MINIO_BUCKET_LOADER!,
        file.name,
      );

      // Convert Uint8Array to Buffer
      const chunks: Uint8Array[] = [];
      for await (const chunk of fileStream) {
        chunks.push(chunk);
      }
      const fileBuffer = Buffer.concat(chunks);

      // Calculate MD5
      minioFilesMap[file.name.replace("reference/", "")] =
        computeMD5(fileBuffer);
    }
    const files = await fs.promises.readdir(REF_DATA_DIR);

    // Compare and upload files if necessary
    await Promise.all(
      files.map(async (file) => {
        const filePath = path.join(REF_DATA_DIR, file);
        const fileBuffer = await fs.promises.readFile(filePath);
        const fileHash = computeMD5(fileBuffer);

        // Check if file has changed
        if (minioFilesMap[file] && minioFilesMap[file] === fileHash) {
          console.log(`Ref file ${file} is up to date, skipping upload.`);
          return;
        }

        // Upload data if file has changed
        await putObject(
          process.env.MINIO_BUCKET_LOADER!,
          `reference/${file}`,
          fileBuffer,
        );
        console.log(`Uploaded: ${file}`);
      }),
    );

    console.log("All files checked and updated if necessary.");
    return true;
  } catch (error) {
    console.error("Error uploading files:", error);
    throw new Error("Failed to upload fasta files");
  }
};

// TODO A conserver (Génération fichier référence)
// export const createReferenceFastaFile = async (
//   referenceType: $Enums.is_reference
// ) => {
//   console.log("createReferenceFastaFile", referenceType);
//   let outputFilePath = `${referenceType}.fasta`;

//   try {
//     // Requête pour récupérer les URLs des fichiers FASTA
//     const results = await prisma.sequence.findMany({
//       where: { is_reference: referenceType },
//     });
//     if (results) {
//       // Pour chaque URL récupérée, télécharger et écrire le contenu dans le fichier de sortie
//       for (const { fasta_url } of results) {
//         try {
//           console.log("fasta_url", fasta_url);
//           const fastaBuffer = (await downloadFile(
//             process.env.MINIO_BUCKET_LOADER!,
//             fasta_url
//           )) as Buffer;
//           console.log(fastaBuffer.toString());
//           await fs.appendFile(outputFilePath, fastaBuffer.toString() + "\n");
//         } catch (downloadError) {
//           console.error(
//             `Error downloading or writing ${fasta_url}:`,
//             downloadError
//           );
//         }
//       }

//       // Lire le contenu du fichier avant de le téléverser
//       let fileContent;
//       try {
//         fileContent = await fs.readFile(outputFilePath);
//       } catch (readError) {
//         console.error(`Error reading file ${outputFilePath}:`, readError);
//         throw new Error(`Failed to read file ${outputFilePath}`);
//       }

//       // Upload sequence into a bucket
//       try {
//         await putObject(
//           process.env.MINIO_BUCKET_TMP!,
//           `reference/${referenceType}_not_aligned.fasta`,
//           fileContent
//         );
//       } catch (uploadError) {
//         console.error(`Error uploading file to bucket:`, uploadError);
//         throw new Error("Failed to upload fasta files");
//       }
//       // Appel de la fonction callMafftApi
//       try {
//         const mafftApiUrl = "/alignRef";
//         const result = await callMafftApi(
//           mafftApiUrl,
//           outputFilePath,
//           `prefix_${referenceType}`
//         );
//         console.log("MAFFT API result:", result);
//       } catch (mafftError) {
//         console.error("Error calling MAFFT API:", mafftError);
//         throw new Error("Failed to call MAFFT API");
//       }

//       return results;
//     }
//     return null;
//   } catch (error) {
//     console.error("Error in createReferenceFastaFile:", error);
//     throw new Error("Failed to create the reference FASTA file.");
//   } finally {
//     // Supprimer le fichier temporaire
//     try {
//       await fs.unlink(outputFilePath);
//       console.log(`Temporary file ${outputFilePath} deleted.`);
//       await deleteObject(
//         process.env.MINIO_BUCKET_LOADER!,
//         `reference/${referenceType}_not_aligned.fasta`
//       );
//       console.log(
//         `Temporary file ${referenceType}_not_aligned.fasta deleted in upload bucket.`
//       );
//     } catch (unlinkError) {
//       console.error(
//         `Error deleting temporary file ${outputFilePath}:`,
//         unlinkError
//       );
//     }
//   }
// };

// TODO to keep when thesaurus integrations start
// export const convertThesaurusValuesToIds = async (
//   temporaryTableName: string
// ) => {
//   try {
//     const query = `
//       WITH thesaurus_host_cte AS (
//         SELECT LOWER(name) AS lower_name, id
//         FROM public.thesaurus_host
//       ),
//       thesaurus_sample_cte AS (
//         SELECT LOWER(name) AS lower_name, id
//         FROM public.thesaurus_sample
//       ),
//       thesaurus_sequencing_technology_cte AS (
//         SELECT LOWER(name) AS lower_name, id
//         FROM public.thesaurus_sequencing_technology
//       ),
//       thesaurus_sequencing_strategy_cte AS (
//         SELECT LOWER(name) AS lower_name, id
//         FROM public.thesaurus_sequencing_strategy
//       )
//       UPDATE import_temp.${temporaryTableName} temp
//       SET
//         thesaurus_host_id = (
//           SELECT id
//           FROM thesaurus_host_cte
//           WHERE lower_name = LOWER(temp.host)
//           LIMIT 1
//         ),
//         thesaurus_sample_id = (
//           SELECT id
//           FROM thesaurus_sample_cte
//           WHERE lower_name = LOWER(temp.sample_type)
//           LIMIT 1
//         ),
//         thesaurus_sequencing_technology_id = (
//           SELECT id
//           FROM thesaurus_sequencing_technology_cte
//           WHERE lower_name = LOWER(temp.sequencing_technology)
//           LIMIT 1
//         ),
//         thesaurus_sequencing_strategy_id = (
//           SELECT id
//           FROM thesaurus_sequencing_strategy_cte
//           WHERE lower_name = LOWER(temp.sequencing_strategy)
//           LIMIT 1
//         )
//       WHERE temp.host IS NOT NULL
//         OR temp.sample_type IS NOT NULL
//         OR temp.sequencing_technology IS NOT NULL
//         OR temp.sequencing_strategy IS NOT NULL;
//     `;

//     const result = await prisma.$queryRaw`${Prisma.raw(query)}`;
//     return result;
//   } catch (error) {
//     console.error(
//       `Failed to update thesaurus values in import_temp.${temporaryTableName}`,
//       error
//     );
//     throw new Error(
//       `Failed to update thesaurus values in import_temp.${temporaryTableName}`
//     );
//   }
// };

export const insertInSequenceTable = async (temporaryTableName: string) => {
  await verifyServerSession();
  // Insert data from temporary table in sequence table
  try {
    const query = `SELECT * FROM import_temp.${temporaryTableName};`;
    const result = await prisma.$queryRaw`${Prisma.raw(query)}`;

    // Map database enum values to TypeScript enum values for WNV genotypes
    const wnvGenotypeMapping: Record<string, string> = {
      "1": "WNV_1",
      "2": "WNV_2",
      "3": "WNV_3",
      "4": "WNV_4",
      "5": "WNV_5",
      "6": "WNV_6",
      "7": "WNV_7",
      "8": "WNV_8",
      "9": "WNV_9",
    };

    const mappedResult = (result as any[]).map((row: any) => {
      // Map WNV genotype values
      if (row.genotype && wnvGenotypeMapping[row.genotype]) {
        return {
          ...row,
          genotype: wnvGenotypeMapping[row.genotype],
        };
      }
      return row;
    });
    // Remove id from the object to ensure it is not inserted in the database
    const data = mappedResult.map((row) => {
      const clean: any = {};
      for (const key of Object.keys(row)) {
        if (key !== "id") {
          clean[key] = row[key];
        }
      }
      return clean;
    }) as any[];
    return await prisma.sequence.createMany({
      data: data,
    });
  } catch (error) {
    console.error(
      `Failed to create many sequence table with the import_temp.${temporaryTableName} table`,
      error,
    );
    throw new Error(
      `Failed to create many sequence table with the import_temp.${temporaryTableName} table`,
    );
  }
};

/**
 * Update the status of an import.
 *
 * @param {number} importId - The ID of the import to update.
 * @param {string} status - The new status of the import.
 *
 * @throws {Error} If the import status cannot be updated.
 */

export const updateImportStatus = async (importId: number, status: status) => {
  const { loggedInUuid } = await verifyServerSession();
  try {
    await prisma.import.update({
      where: { ID: importId, User_Keycloak_ID: loggedInUuid },
      data: { Status: status },
    });
  } catch (error: any) {
    console.error("Failed to update import status", error);
    throw new Error("Failed to update import status");
  }
};

export const updateDataInTempTable = async (
  id: string,
  columnToUpdate: string,
  dataToInsert: any,
  temporaryTableName: string,
) => {
  await verifyServerSession();
  try {
    // Whitelist of allowed columns to prevent SQL injection
    const ALLOWED_COLUMNS = getTemporaryTableColumns();

    if (!ALLOWED_COLUMNS.includes(columnToUpdate)) {
      throw new Error(`Invalid column name: ${columnToUpdate}`);
    }

    // Define columns that need type casting (enums and custom types)
    const enumColumns: Record<string, string> = {
      genotype: "genotype",
      severity: "severity",
      sample_type: "sampletype",
      host: "host",
      sequencing_technology: "sequencing_technology",
      sequencing_strategy: "sequencing_strategy",
      infection_loc_admin0: "country_code",
      sampling_loc_admin0: "country_code",
      travel_loc_admin0: "country_code",
      virus_species: "viruses",
    };

    // Use parameterized query to prevent SQL injection
    // Add type casting for enum columns
    let nbRows;
    if (enumColumns[columnToUpdate]) {
      const enumType = enumColumns[columnToUpdate];
      nbRows = await prisma.$executeRaw`
        UPDATE import_temp.${Prisma.raw(temporaryTableName)}
        SET ${Prisma.raw(columnToUpdate)}=${dataToInsert}::${Prisma.raw(enumType)}
        WHERE seq_id=${id}
      `;
    } else {
      nbRows = await prisma.$executeRaw`
        UPDATE import_temp.${Prisma.raw(temporaryTableName)}
        SET ${Prisma.raw(columnToUpdate)}=${dataToInsert}
        WHERE seq_id=${id}
      `;
    }
  } catch (error: any) {
    console.error(
      `Failed to set: ${columnToUpdate}=${dataToInsert} for ${id}`,
      error,
    );
    throw new Error(
      `Failed to set: ${columnToUpdate}=${dataToInsert} for ${id}`,
    );
  }
};

export const callNextClade = async (
  fastaFilePath: FastaFile[],
  prefix: string,
) => {
  await verifyServerSession();
  try {
    // Check if service is running
    await checkIfServiceIsRunning();
    // Create an array of fetch promises

    const promises = fastaFilePath.map(async (item) => {
      const body = {
        fasta_url: item.fasta_url,
        seq_id: item.seq_id,
        prefix,
      };

      return fetch(`${process.env.ANALYSIS_URL}/nextclade`, {
        method: "POST",
        body: JSON.stringify(body),
        headers: {
          "Content-Type": "application/json",
        },
      });
    });

    const results = await Promise.all(promises);
    let resultNextclade = await Promise.all(
      results.map((result: any) => result.json()),
    );
    return resultNextclade;
  } catch (error) {
    console.error("Error calling NextClade API:", error);
    throw error;
  }
};

export const callMafftApi = async (
  fastaFilePath: FastaFile[],
  stop_codon: boolean,
  prefix: string,
) => {
  await verifyServerSession();
  try {
    // Check if service is running
    await checkIfServiceIsRunning();
    // Create an array of fetch promises
    const promises = fastaFilePath.map(async (item) => {
      const body = {
        input_fasta_file: item.fasta_url,
        input_fasta_id: item.seq_id,
        reference_fasta_file: item.genotype ? item.genotype : null,
        prefix,
        stop_codon,
      };

      return fetch(`${process.env.ANALYSIS_URL}/align`, {
        method: "POST",
        body: JSON.stringify(body),
        headers: {
          "Content-Type": "application/json",
        },
      });
    });

    const results = await Promise.all(promises);
    let alignedFilesPathList = await Promise.all(
      results.map((result: any) => result.json()),
    );
    return alignedFilesPathList;
  } catch (error) {
    console.error("Error calling MAFFT API:", error);
    throw error;
  }
};

export const callIqTreeApi = async (
  fastaFilePath: { path: string; id: string }[],
  prefix: string,
) => {
  await verifyServerSession();
  try {
    // Check if service is running
    await checkIfServiceIsRunning();
    const promises = fastaFilePath.map(({ path, id }) => {
      return fetch(process.env.ANALYSIS_URL + "/build_tree", {
        method: "POST",
        body: JSON.stringify({
          input_fasta_file: path,
          seq_id: id,
          prefix: prefix,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      });
    });
    const results = await Promise.all(promises);
    let treeFilesPathList = await Promise.all(
      results.map((result: any) => result.json()),
    );
    return treeFilesPathList;
  } catch (error) {
    console.error("Error calling IQTree API:", error);
    throw error;
  }
};

export const generateZipFile = async (
  fileList: string[],
  prefix: string,
  zipType: "aln" | "tree" | "aln_error" | "blast" | "nextclade" | "datasets",
  isRepository: boolean = false, // isRepository is true when fileList is a repository path
) => {
  await verifyServerSession();
  try {
    // Check if service is running
    await checkIfServiceIsRunning();
    const response = await fetch(process.env.ANALYSIS_URL + "/zip", {
      method: "POST",
      body: JSON.stringify({
        files: fileList,
        prefix: prefix,
        zipType: zipType,
        isRepository: isRepository,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      console.log(response.json());
      throw new Error(`Failed to zip files: ${response.statusText}`);
    }
    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Error calling ZIP API:", error);
    throw error;
  }
};

export const getTempZipFileUrl = async (
  zipPath: string,
  zipType: "aln" | "tree" | "aln_error" | "blast" | "nextclade" | "datasets",
  isFullPath: boolean = false,
) => {
  await verifyServerSession();
  try {
    const res = await presignedGetObjectTmp(
      isFullPath ? zipPath : zipPath + "/" + zipPath + "_" + zipType + ".zip",
    );
    return res;
  } catch (error) {
    console.error("Error downloading zip file:", error);
    throw error;
  }
};

export const callSerotypeDeterminationApi = async (
  mldistFilePath: string,
  prefix: string,
) => {
  await verifyServerSession();
  try {
    // Check if service is running
    await checkIfServiceIsRunning();
    const response = await fetch(process.env.ANALYSIS_URL + "/find_serotype", {
      method: "POST",
      body: JSON.stringify({
        input_fasta_file: mldistFilePath,
        reference_fasta_file: null,
        prefix: prefix,
        input_fasta_id: null,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      console.log(response.json());
      throw new Error(
        `Failed to call Serotype determination API: ${response.statusText}`,
      );
    }
    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Error calling Serotype determination API:", error);
    throw error;
  }
};

export const fetchDataWithGenotype = async (temporaryTableName: string) => {
  await verifyServerSession();
  try {
    // 1. Récupérer les données de la table temporaire
    const tempData: FastaFile[] = await timeoutPromise(
      prisma.$queryRaw`SELECT seq_id, fasta_url, serotype FROM import_temp.${Prisma.raw(temporaryTableName)}`,
    );
    // 2. Récupérer le génotype correspondant au sérotype
    const dataWithGenotype = await Promise.all(
      tempData.map(async (row) => {
        const concordance = await prisma.serotypeGenotypeConcordance.findFirst({
          where: { serotype: row.serotype },
          select: { genotype: true },
        });

        let result = { ...row };
        if (concordance?.genotype) result.genotype = concordance?.genotype;

        return result;
      }),
    );

    return dataWithGenotype;
  } catch (error) {
    console.error("Failed to fetch data with genotype", error);
    throw error;
  }
};

export const callGenotypeDeterminationApi = async (
  treeFilePath: string,
  prefix: string,
) => {
  await verifyServerSession();
  try {
    // Check if service is running
    await checkIfServiceIsRunning();
    const response = await fetch(process.env.ANALYSIS_URL + "/find_genotype", {
      method: "POST",
      body: JSON.stringify({
        input_fasta_file: treeFilePath,
        reference_fasta_file: null,
        prefix: prefix,
        input_fasta_id: null,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (!response.ok) {
      console.log(response.json());
      throw new Error(
        `Failed to call Genotype determination API: ${response.statusText}`,
      );
    }
    const result = await response.json();
    return result;
  } catch (error) {
    console.error("Error calling Genotype determination API:", error);
    throw error;
  }
};

// TODO A conserver (Génération fichier référence)
// export const uploadReferenceFile = async (
//   csvData: MetaDataRow[] | [],
//   fastaData: { id: string; path: string }[]
// ) => {
//   // Retrieve path from fastaData based on seq_id (from csvData)
//   const getPathFromSeqId = (
//     seqId: string,
//     fastaData: { id: string; path: string }[]
//   ): string | undefined => {
//     const pathObject = fastaData.find((obj) => obj.id === seqId);
//     return pathObject ? pathObject.path : undefined;
//   };

//   // Retrieve file name from path (right part of the path after /)
//   const getFileNameFromPath = (path: string): string => {
//     const parts = path.split("/");
//     return parts[parts.length - 1];
//   };

//   // Get seq_ids from csvData
//   const seqIds: string[] = csvData.map((item) => item.seq_id);

//   try {
//     const copyPromises = seqIds.map((seqId) => {
//       const path = getPathFromSeqId(seqId, fastaData);
//       const fileName = getFileNameFromPath(path!);
//       if (!path) {
//         throw new Error(`Path not found for seq_id: ${seqId}`);
//       }
//       const destinationObject = `reference/${fileName}`;
//       const sourceObject = `upload/${path}`;
//       return copyObject("upload", destinationObject, sourceObject);
//     });

//     const res = await Promise.all(copyPromises);
//     return res;
//   } catch (error) {
//     console.error("Error uploading reference file:", error);
//     throw error;
//   }
// };

export const checkIfRefExists = async (fileName: string): Promise<boolean> => {
  await verifyServerSession();
  try {
    const exists = await timeoutPromise(refExists(fileName));
    return exists;
  } catch (error) {
    console.error("Error checking if reference file exists:", error);
    throw error;
  }
};

const checkIfServiceIsRunning = async (): Promise<boolean> => {
  console.log("Checking if service is running...");
  console.log(process.env.ANALYSIS_URL + "/health");
  try {
    const isRunning = await timeoutPromise(
      fetch(process.env.ANALYSIS_URL + "/health", {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      }),
    );
    if (isRunning.status === "healthy") {
      return true;
    }
    return false;
  } catch (error) {
    console.error("Error checking if service is running:", error);
    throw error;
  }
};

export const getCsvFile = async (
  prefix: string,
  data: any[], // can be array of objects (metadata) or array of strings (arbogen_id)
  fileName: string,
) => {
  await verifyServerSession();
  try {
    // Check if service is running
    await checkIfServiceIsRunning();
    // 1. Generate CSV content
    let csvHeader = "";
    let csvRows = "";
    if (!data || data.length === 0) {
      throw new Error("No data provided");
    }

    if (data.length === 0) {
      console.log("No data provided");
      csvHeader = "";
      csvRows = "";
    } else if (typeof data[0] === "object" && data[0] !== null) {
      console.log("Array of objects (metadata)");
      // Array of objects (metadata)
      const keys = Object.keys(data[0]);
      csvHeader = keys.join(",") + "\n";
      csvRows =
        data
          .map((obj) =>
            keys
              .map((key) => {
                const val = obj[key];
                // Convert boolean to integer (1/0)
                if (typeof val === "boolean") {
                  return val ? "1" : "0";
                }
                // Escape quotes and commas
                if (val === null || val === undefined) return "";
                if (
                  typeof val === "string" &&
                  (val.includes(",") || val.includes('"') || val.includes("\n"))
                ) {
                  return '"' + val.replace(/"/g, '""') + '"';
                }
                return val;
              })
              .join(","),
          )
          .join("\n") + "\n";
    } else {
      // Array of strings (arbogen_id)
      csvHeader = "arbogen_id\n";
      csvRows = data.map((id) => `${id}\n`).join("");
    }
    const csvContent = csvHeader + csvRows;

    // 2. Create a Blob or Buffer from the CSV content
    const csvBuffer = Buffer.from(csvContent, "utf-8");

    // 3. Upload the CSV file directly to MinIO
    const objectPath = `${prefix}/${fileName}.csv`;
    try {
      await timeoutPromise(
        putObject(process.env.MINIO_BUCKET_TMP!, objectPath, csvBuffer),
      );
    } catch (error) {
      console.error("Error uploading CSV file:", error);
      throw error;
    }

    // 4. Generate presigned GET URL for download
    let presignedUrl: string;
    try {
      presignedUrl = await timeoutPromise(
        presignedGetObjectTmp(objectPath, 3600),
      );
    } catch (error) {
      console.error("Error getting presigned URL:", error);
      throw error;
    }

    // 5. Return the presigned GET URL
    return { url: presignedUrl, status: "success" };
  } catch (error) {
    console.error("Error generating or uploading CSV file:", error);
    throw error;
  }
};

export const getMultiFastaFile = async (
  prefix: string,
  data: SequenceToExport[] | { fasta_url: string }[],
  fileName: string,
) => {
  await verifyServerSession();
  try {
    const fasta_url_list = data.map((item) => item.fasta_url);
    // Check if service is running
    await checkIfServiceIsRunning();
    const response = await fetch(
      process.env.ANALYSIS_URL + "/generate_multi_fasta",
      {
        method: "POST",
        body: JSON.stringify({
          prefix: prefix,
          urls: fasta_url_list,
          output_file: fileName,
        }),
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      throw new Error(
        `Failed to generate multi fasta file: ${response.statusText}`,
      );
    }
    const result = await response.json();
    if (result.status === "success") {
      try {
        const res = await presignedGetObjectTmp(result.multi_fasta_path);
        return res;
      } catch (error) {
        console.error("Error downloading zip file:", error);
        throw error;
      }
    }
    return result;
  } catch (error) {
    console.error("Error calling multi fasta generator API:", error);
    throw error;
  }
};

/**
 * Generate a zip file containing multi fasta files for each dataset.
 *
 * @param datasets Record of dataset id and its properties (name, fasta_urls)
 * @param prefix String to prefix the filename with
 * @returns Promise that resolves to a downloadable zip file
 */

export const generateZipFromDatasets = async (
  datasets: Record<
    number,
    {
      name: string;
      fasta_urls?: string[];
      arbogen_id?: string[];
      sequences?: any[];
    }
  >,
  prefix: string,
  zipType: "sequences" | "metadata" | "arbogen_id" | "both",
) => {
  await verifyServerSession();
  try {
    const hasData = Object.values(datasets).some((dataset) => {
      if (zipType === "sequences") {
        return dataset.fasta_urls && dataset.fasta_urls.length > 0;
      }
      if (zipType === "metadata") {
        return dataset.sequences && dataset.sequences.length > 0;
      }
      if (zipType === "arbogen_id") {
        return dataset.arbogen_id && dataset.arbogen_id.length > 0;
      }
      if (zipType === "both") {
        return (
          (dataset.fasta_urls && dataset.fasta_urls.length > 0) ||
          (dataset.sequences && dataset.sequences.length > 0)
        );
      }
      return false;
    });

    if (!hasData) {
      return undefined;
    }

    // Use Promise.all to wait for all file creation to finish before zipping
    const allPromises: Promise<any>[] = [];

    if (zipType === "sequences" || zipType === "both") {
      allPromises.push(
        ...Object.entries(datasets).map(async ([id, dataset]) => {
          const fasta_url_list =
            dataset.fasta_urls?.map((url) => ({
              fasta_url: url,
            })) ?? [];
          // For "both" type, organize files in dataset subfolders
          const filePrefix =
            zipType === "both" ? `${prefix}/${dataset.name}` : prefix;
          const fileName = `${dataset.name}_sequences`;
          const response = await getMultiFastaFile(
            filePrefix,
            fasta_url_list,
            fileName,
          );
          return response?.multi_fasta_path;
        }),
      );
    }
    if (zipType === "metadata" || zipType === "both") {
      allPromises.push(
        ...Object.entries(datasets).map(async ([id, dataset]) => {
          const metadata_list = dataset.sequences ?? [];
          // For "both" type, organize files in dataset subfolders
          const filePrefix =
            zipType === "both" ? `${prefix}/${dataset.name}` : prefix;
          const fileName = `${dataset.name}_metadata`;
          const response = await getCsvFile(
            filePrefix,
            metadata_list,
            fileName,
          );
          return response?.url;
        }),
      );
    }
    if (zipType === "arbogen_id") {
      allPromises.push(
        ...Object.entries(datasets).map(async ([id, dataset]) => {
          const arbogen_id_list = dataset.arbogen_id ?? [];
          // For "both" type, organize files in dataset subfolders
          const filePrefix = prefix;
          const fileName = `${dataset.name}_IDs`;
          const response = await getCsvFile(
            filePrefix,
            arbogen_id_list,
            fileName,
          );
          return response?.url;
        }),
      );
    }

    // Wait for all file creation to finish before zipping
    await Promise.all(allPromises);
    // All files are created, we can zip them
    let zipResponse;
    try {
      zipResponse = await generateZipFile([prefix], prefix, "datasets", true);
    } catch (error) {
      console.error("Error in generateZipFile:", error);
      throw error;
    }
    if (zipResponse.status === "success") {
      try {
        const res = await presignedGetObjectTmp(zipResponse.zip_path);
        return res;
      } catch (error) {
        console.error("Error downloading zip file:", error);
        throw error;
      }
    }
  } catch (error) {
    console.error("Error calling generateZipFromDatasets API:", error);
    throw error;
  }
};
// TODO to keep when thesaurus integrations start
// export const getThesaurusData = async (data_name: string) => {
//   try {
//     // Mappage des noms de données aux modèles Prisma
//     const modelMapping: { [key: string]: string } = {
//       sample: "thesaurus_sample",
//       host: "thesaurus_host",
//       sequencing_technology: "thesaurus_sequencing_technology",
//       sequencing_strategy: "thesaurus_sequencing_strategy",
//     };

//     // Vérifier si data_name existe dans le mappage
//     const modelName = modelMapping[data_name];
//     if (!modelName) {
//       throw new Error(`Model name invalid`);
//     }

//     // Utiliser le modèle dynamique dans la requête Prisma
//     const samples = await (prisma as any)[modelName].findMany({
//       select: {
//         name: true,
//       },
//     });

//     const sampleNames = samples.map((sample: { name: any }) => sample.name);
//     console.log("sampleNames", sampleNames);
//     return sampleNames;
//     // res.status(200).json(sampleNames);
//   } catch (error) {
//     console.error(error);
//     console.error("Error to retrieve datas:", error);
//   }
// };

export const getImportMetrics = async () => {
  try {
    const importCount = await prisma.import.count({
      where: {
        Status: "COMPLETED",
      },
    });
    const sequenceCount = await prisma.sequence.count();
    const distinctLocationCount = await prisma.sequence.groupBy({
      by: ["infection_loc_admin0"],
      _count: {
        infection_loc_admin0: true,
      },
    });
    return {
      importCount,
      sequenceCount,
      distinctLocationCount: distinctLocationCount.length,
    };
  } catch (error) {
    console.error("Error to retrieve import metrics:", error);
    throw error;
  }
};

/**
 * Get the virus species of all sequences in an import.
 * Returns null if import is empty or if sequences have different virus species.
 *
 * @param importId ID of the Import
 * @returns The virus species if all sequences have the same one, null otherwise
 */
export const getImportVirusSpecies = async (
  importId: number,
): Promise<viruses | null> => {
  const { loggedInUuid } = await verifyServerSession();
  const sequences = await prisma.sequence.findMany({
    where: {
      import_id: importId,
      import: {
        User_Keycloak_ID: loggedInUuid,
      },
    },
    select: {
      virus_species: true,
    },
  });

  if (sequences.length === 0) {
    return null;
  }

  const uniqueSpecies = new Set(sequences.map((seq) => seq.virus_species));

  if (uniqueSpecies.size === 1) {
    return uniqueSpecies.values().next().value ?? null;
  }

  return null;
};
